import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, replyModeOf } from '../../store';
import { Email } from '../../types';
import { apiFetch } from '../../utils/api';
import Composer from '../Composer';

// Read an email + attachments + Reply / Reply-all / Forward (with AI, via the shared Composer).
// Used by the Lead email preview and the dashboard Starred preview. Two authorization paths:
//   1. mailbox owner  — full email via loadEmail (GET /emails/:id)
//   2. Leads module   — full email package returned by GET /leads/emails/:rowId/view
//      (marketing staff don't own the office mailbox; pass `leadRowId` to enable this path).
// z-index 1900 so the Composer (2000) and its AI modal (3000) sit on top.

type Snapshot = {
  subject?: string; fromName?: string; fromEmail?: string; date?: any; bodyHtml?: string; bodyText?: string;
  // extended fields from the lead view endpoint
  available?: boolean; id?: string; accountId?: string; accountEmail?: string;
  toList?: string[]; ccList?: string[]; attachments?: any[]; detectedLang?: string; folder?: string;
};
const nl2br = (s: string) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
const fmtWhen = (d: any): string => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date((typeof d === 'number' ? (d > 1e12 ? d : d * 1000) : Date.parse(d)) || Date.now());
  try { return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(date); } catch { return ''; }
};
const fmtBytes = (n: number) => n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`;
// The "opened" image on our Sent copies — every email the system sends carries one (Calem, Bugify
// #fcd1ede7) — is taken out before drawing, so reading your own email never counts as the recipient
// opening it. The same pattern as HtmlFrame's OPEN_PIXEL (EmailDetail) and the server's stripOpenPixel.
const OPEN_PIXEL = /<img\b[^>]*\/api\/public\/peak\/[^"'\s>/]+\/open\.gif[^>]*>/gi;

function snapshotToEmail(sn: Snapshot): Email | null {
  if (!sn.available || !sn.id || !sn.accountId) return null;
  return {
    id: sn.id, accountId: sn.accountId, accountEmail: sn.accountEmail,
    subject: sn.subject || '', from: { name: sn.fromName || '', email: sn.fromEmail || '' },
    to: sn.toList || [], cc: sn.ccList || [], bcc: [],
    date: new Date((Number(sn.date) || 0) * 1000), lang: sn.detectedLang,
    bodyOriginal: sn.bodyHtml || (sn.bodyText ? nl2br(sn.bodyText) : ''),
    bodyTranslated: {}, attachments: sn.attachments || [],
    unread: false, starred: false, folder: sn.folder || 'inbox', labels: [],
  } as Email;
}

async function downloadVia(url: string, filename: string) {
  const token = localStorage.getItem('mf_token');
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const res = await apiFetch(`${apiBase}${url}`, { headers: { Authorization: `Bearer ${token}` } });   // a restart's failure brings up the notice
  if (!res.ok) return;
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(href);
}

export default function EmailViewer({ emailId, snapshot, leadRowId, onClose }: { emailId: string; snapshot?: Snapshot; leadRowId?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { loadEmail, loadAccounts, accounts, replyAllDefault } = useStore();
  const [full, setFull] = useState<Email | null>(null);
  const [ownerPath, setOwnerPath] = useState(false);   // which attachment endpoint to use
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'reply' | 'replyAll' | 'forward' | null>(null);

  useEffect(() => {
    if (!accounts.length) loadAccounts().catch(() => {});
    let alive = true;
    (async () => {
      // Owner path first (full row incl. translations); lead package as the fallback.
      if (emailId) {
        const e = await loadEmail(emailId).catch(() => null);
        if (e && alive) { setFull(e); setOwnerPath(true); setLoading(false); return; }
      }
      const viaLead = snapshot ? snapshotToEmail(snapshot) : null;
      if (alive) { if (viaLead) setFull(viaLead); setLoading(false); }
    })();
    return () => { alive = false; };
  }, [emailId, leadRowId]); // eslint-disable-line

  const subject = full?.subject || snapshot?.subject || '(no subject)';
  const fromName = full?.from.name || snapshot?.fromName || '';
  const fromEmail = full?.from.email || snapshot?.fromEmail || '';
  const when = fmtWhen(full?.date || snapshot?.date);
  const toLine = (full?.to || []).join(', ');
  const ccLine = (full?.cc || []).join(', ');
  const bodyHtml = (full ? full.bodyOriginal : (snapshot?.bodyHtml || (snapshot?.bodyText ? nl2br(snapshot.bodyText) : ''))).replace(OPEN_PIXEL, '');
  const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank" rel="noopener noreferrer"><style>body{font-family:Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;color:#222;margin:14px}img{max-width:100%}a{color:#3297FF}</style></head><body>${bodyHtml || '<p style="color:#888">—</p>'}</body></html>`;
  const canReply = !!full;
  const atts: any[] = full?.attachments || snapshot?.attachments || [];
  const attUrl = (att: any) => ownerPath || !leadRowId
    ? `/emails/${full?.id || emailId}/attachments/${att.id}`
    : `/leads/emails/${leadRowId}/attachments/${att.id}`;

  const actBtn = (icon: string, label: string, m: 'reply' | 'replyAll' | 'forward') => (
    <button onClick={() => setMode(replyModeOf(m))} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
      <i className={`ti ti-${icon}`} style={{ fontSize: 14 }} />{label}
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={e => e.stopPropagation()} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 780, maxWidth: '95vw', height: '86vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15.5, fontWeight: 800, color: 'var(--text-primary)' }}>{subject}</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}><b style={{ color: 'var(--text-secondary)' }}>{fromName || fromEmail}</b>{fromEmail ? ` <${fromEmail}>` : ''}{when ? ` · ${when}` : ''}</div>
            {toLine && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>To: {toLine}{ccLine ? ` · CC: ${ccLine}` : ''}</div>}
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 18 }}><i className="ti ti-x" /></button>
        </div>
        <div style={{ flex: 1, minHeight: 0, background: '#fff' }}>
          {loading && !bodyHtml ? <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div>
            : <iframe title="email" srcDoc={srcDoc} sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" style={{ width: '100%', height: '100%', border: 'none' }} />}
        </div>
        {atts.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border)', padding: '10px 20px', display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 120, overflowY: 'auto' }}>
            {atts.map((att: any) => (
              <button key={att.id} onClick={() => downloadVia(attUrl(att), att.name || att.filename || 'attachment')} title={att.name || att.filename}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 9, maxWidth: 230, cursor: 'pointer' }}>
                <i className="ti ti-paperclip" style={{ fontSize: 15, color: 'var(--accent)', flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name || att.filename}</span>
                {Number(att.size) > 0 && <span style={{ fontSize: 10, color: 'var(--text-tertiary)', flexShrink: 0 }}>{fmtBytes(Number(att.size))}</span>}
                <i className="ti ti-download" style={{ fontSize: 13, color: 'var(--accent)', flexShrink: 0 }} />
              </button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
          {canReply ? <>
            {/* No Reply while the Reply all switch is on — it would only open as Reply All (report 643e2141) */}
            {!replyAllDefault && actBtn('corner-up-left', t('ev_reply'), 'reply')}
            {actBtn('corner-up-left-double', t('ev_reply_all'), 'replyAll')}
            {actBtn('corner-up-right', t('ev_forward'), 'forward')}
          </> : <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('ev_no_reply')}</span>}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 9, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cl_close')}</button>
        </div>
      </div>
      {mode && full && <Composer email={full} mode={mode} onClose={() => setMode(null)} />}
    </div>
  );
}
